import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Input,
  Menu,
  message,
  Popconfirm,
  Popover,
  Row,
  Space,
  Table,
  Tabs,
  Tag,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FilterOutlined,
  PlusCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { deleteDoc, doc } from "firebase/firestore";
import { firestore } from "../../../../configs/FirebaseConfig";
import { FIRESTORE_PRICE_LISTS_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../../constants/CompanyConstants";
import { NETHERLANDS_COUNTRY_CODE, PRICING_COUNTRIES } from "../../../../constants/PricingConstants";
import EllipsisDropdown from "../../../../components/shared-components/EllipsisDropdown";
import Flex from "../../../../components/shared-components/Flex";
import utils from "../../../../utils";
import { usePriceLists } from "../../../../data/hooks/firestore/usePriceLists";
import { useProducts } from "../../../../data/hooks/firestore/useProducts";
import { useCustomers } from "../../../../data/hooks/firestore/useCustomers";
import Loading from "../../../../components/shared-components/Loading";
import AddPriceListModal from "./modals/AddPriceListModal";
import AddCountryPriceListModal from "./modals/AddCountryPriceListModal";
import EditPriceListModal from "./modals/EditPriceListModal";
import EditPriceListCustomersModal from "./modals/EditPriceListCustomersModal";
import { isMobile } from "../../../../functions/browser";
import { formatPrice } from "../../../../functions/formatters";
import { CURRENCY } from "../../../../constants/DeliveryConstants";
import styled from "styled-components";
import useIsMobile from "../../../../hooks/useIsMobile";

const PriceLists = () => {
  const { t, i18n } = useTranslation();
  const { priceLists, isLoading: priceListsLoading } = usePriceLists();
  const { products, isLoading: productsLoading } = useProducts();
  const { customers, isLoading: customersLoading } = useCustomers();

  const [activeTab, setActiveTab] = useState("customer");
  const [searchText, setSearchText] = useState("");
  const [filteredPriceLists, setFilteredPriceLists] = useState([]);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addCountryModalVisible, setAddCountryModalVisible] = useState(false);
  const [editPriceListModalVisible, setEditPriceListModalVisible] = useState(false);
  const [editCustomersModalVisible, setEditCustomersModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedPriceList, setSelectedPriceList] = useState(null);
  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);
  const isMobileView = useIsMobile();

  const customerPriceLists = useMemo(() => priceLists.filter((pl) => pl.type === "customer" || !pl.type), [priceLists]);

  const countryPriceLists = useMemo(() => priceLists.filter((pl) => pl.type === "country"), [priceLists]);

  React.useEffect(() => {
    applyFilters(searchText);
  }, [priceLists, activeTab]);

  const applyFilters = (searchValue) => {
    const source = activeTab === "customer" ? customerPriceLists : countryPriceLists;
    let filtered = [...source];

    if (searchValue) {
      filtered = filtered.filter((priceList) => priceList.name.toLowerCase().includes(searchValue.toLowerCase()));
    }

    setFilteredPriceLists(filtered);
  };

  const onSearch = (value) => {
    setSearchText(value);
    applyFilters(value);
  };

  const showDrawer = (priceList) => {
    setSelectedPriceList(priceList);
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setTimeout(() => {
      setSelectedPriceList(null);
    }, 300);
  };

  // Get real-time priceList data for drawer
  const livePriceList = useMemo(() => {
    if (!selectedPriceList) return null;
    return priceLists.find((pl) => pl.id === selectedPriceList.id) || selectedPriceList;
  }, [priceLists, selectedPriceList]);

  const handleDelete = async (priceList) => {
    try {
      // Check if any customers are using this price list
      const assignedCustomers = customers.filter((c) => c.priceListId === priceList.id);

      if (assignedCustomers.length > 0) {
        message.error(t("notifications.cannot_delete_price_list_with_customers", { count: assignedCustomers.length }));
        return;
      }

      await deleteDoc(
        doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_PRICE_LISTS_TABLE, priceList.id),
      );

      message.success(t("notifications.price_list_deleted"));
    } catch (error) {
      console.error("Error deleting price list:", error);
      message.error(t("notifications.error_deleting_price_list"));
    }
  };

  const getCountryLabel = (countryCode) => {
    const country = PRICING_COUNTRIES.find((c) => c.code === countryCode);
    return country ? i18n.t(country.label) : countryCode;
  };

  const getCustomerCount = (priceListId) => {
    return customers.filter((c) => c.priceListId === priceListId).length;
  };

  const getCustomPriceCount = (priceList) => {
    return Object.keys(priceList.customPrices || {}).length;
  };

  // Get assigned customers for selected price list
  const assignedCustomers = useMemo(() => {
    if (!livePriceList) return [];
    return customers.filter((c) => c.priceListId === livePriceList.id);
  }, [customers, livePriceList]);

  // Get products with custom prices for selected price list
  const customPricedProducts = useMemo(() => {
    if (!livePriceList || !livePriceList.customPrices) return [];

    const customPriceProductIds = Object.keys(livePriceList.customPrices);
    return products
      .filter((p) => customPriceProductIds.includes(p.id))
      .map((p) => ({
        ...p,
        customPrice: livePriceList.customPrices[p.id],
      }));
  }, [livePriceList, products]);

  const dropdownMenu = (priceList) => {
    const isCountryList = priceList.type === "country";
    return (
      <Menu
        items={[
          {
            key: "view",
            label: t("view_details"),
            icon: <EyeOutlined />,
            onClick: () => showDrawer(priceList),
          },
          {
            key: "edit",
            label: t("edit_price_list"),
            icon: <EditOutlined />,
            onClick: () => {
              setSelectedPriceList(priceList);
              setEditPriceListModalVisible(true);
            },
          },
          ...(!isCountryList ? [] : []),
          {
            key: "delete",
            label: (
              <Popconfirm
                placement="bottom"
                title={t("confirm_delete_price_list", { name: priceList.name })}
                onConfirm={() => handleDelete(priceList)}
                okText={t("yes")}
                cancelText={t("no")}
              >
                <span>{t("delete")}</span>
              </Popconfirm>
            ),
            icon: <DeleteOutlined />,
          },
        ]}
      />
    );
  };

  const customerTableColumns = [
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => utils.antdTableSorter(a, b, "name"),
      render: (name, record) => (
        <a onClick={() => showDrawer(record)} style={{ fontWeight: 500 }}>
          {name}
        </a>
      ),
    },
    {
      title: t("assigned_customers"),
      key: "customers",
      width: 200,
      render: (_, record) => {
        const count = getCustomerCount(record.id);
        return <Tag color={count > 0 ? "blue" : "default"}>{count}</Tag>;
      },
      sorter: (a, b) => getCustomerCount(a.id) - getCustomerCount(b.id),
    },
    {
      title: t("custom_prices"),
      key: "customPrices",
      width: 200,
      render: (_, record) => {
        const count = getCustomPriceCount(record);
        return <Tag color={count > 0 ? "green" : "default"}>{count}</Tag>;
      },
      sorter: (a, b) => getCustomPriceCount(a) - getCustomPriceCount(b),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(record)} />
        </div>
      ),
    },
  ];

  const countryTableColumns = [
    {
      title: t("country"),
      dataIndex: "countryCode",
      key: "countryCode",
      sorter: (a, b) => utils.antdTableSorter(a, b, "countryCode"),
      render: (countryCode, record) => (
        <a onClick={() => showDrawer(record)} style={{ fontWeight: 500 }}>
          {getCountryLabel(countryCode)} ({countryCode})
        </a>
      ),
    },
    {
      title: t("custom_prices"),
      key: "customPrices",
      width: 200,
      render: (_, record) => {
        const count = getCustomPriceCount(record);
        return <Tag color={count > 0 ? "green" : "default"}>{count}</Tag>;
      },
      sorter: (a, b) => getCustomPriceCount(a) - getCustomPriceCount(b),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(record)} />
        </div>
      ),
    },
  ];

  const isNetherlandsList =
    livePriceList?.type === "country" && livePriceList?.countryCode === NETHERLANDS_COUNTRY_CODE;

  const customPriceTableColumns = [
    {
      title: t("name"),
      dataIndex: "productName",
      key: "productName",
      sorter: (a, b) => utils.antdTableSorter(a, b, "productName"),
      defaultSortOrder: "descend",
    },
    // For the NL price list the standard price and custom price are always equal (NL is
    // leading), so only a single "Price" column is shown instead of two.
    ...(isNetherlandsList
      ? []
      : [
          {
            title: t("standard_price"),
            dataIndex: "price",
            key: "standardPrice",
            width: 120,
            render: (price) => (
              <span>
                {CURRENCY}
                {formatPrice(price)}
              </span>
            ),
            sorter: (a, b) => parseFloat(a.price) - parseFloat(b.price),
          },
        ]),
    {
      title: isNetherlandsList ? t("price") : t("custom_price"),
      dataIndex: "customPrice",
      key: "customPrice",
      width: 120,
      render: (price) => (
        <span>
          {CURRENCY}
          {formatPrice(price)}
        </span>
      ),
      sorter: (a, b) => parseFloat(a.customPrice) - parseFloat(b.customPrice),
    },
  ];

  const isLoading = priceListsLoading || productsLoading || customersLoading;

  const renderSearchBar = () => (
    <Flex alignItems="center" justifyContent="between" mobileFlex={true}>
      {!isMobileView ? (
        <Space wrap style={{ marginBottom: 8 }}>
          <Input
            value={searchText}
            placeholder={t("search")}
            prefix={<SearchOutlined />}
            onChange={(e) => onSearch(e.target.value)}
            allowClear={true}
            style={{ width: 250 }}
          />
        </Space>
      ) : (
        <Popover
          content={
            <FilterPopoverContent>
              <div>
                <FilterLabel>{t("search")}</FilterLabel>
                <Input
                  value={searchText}
                  placeholder={t("search")}
                  prefix={<SearchOutlined />}
                  allowClear
                  onChange={(e) => onSearch(e.target.value)}
                  size="large"
                />
              </div>
              <FilterButtonsContainer>
                <Button
                  onClick={() => {
                    setSearchText("");
                    applyFilters("");
                    setFilterPopoverVisible(false);
                  }}
                  style={{ flex: 1 }}
                >
                  {t("reset")}
                </Button>
                <Button type="primary" onClick={() => setFilterPopoverVisible(false)} style={{ flex: 1 }}>
                  {t("apply")}
                </Button>
              </FilterButtonsContainer>
            </FilterPopoverContent>
          }
          title={t("filters")}
          trigger="click"
          open={filterPopoverVisible}
          onOpenChange={setFilterPopoverVisible}
          placement="bottomLeft"
        >
          <Button
            icon={<FilterOutlined />}
            style={{
              borderColor: searchText ? "#FF4B33" : "",
              color: searchText ? "#FF4B33" : "",
              marginBottom: 8,
            }}
          />
        </Popover>
      )}
      <Space wrap style={{ marginBottom: 8 }}>
        {activeTab === "customer" ? (
          <Button onClick={() => setAddModalVisible(true)} type="primary" icon={<PlusCircleOutlined />}>
            {t("add_price_list")}
          </Button>
        ) : (
          <Button onClick={() => setAddCountryModalVisible(true)} type="primary" icon={<PlusCircleOutlined />}>
            {t("add_country_price_list")}
          </Button>
        )}
      </Space>
    </Flex>
  );

  return (
    <>
      <Row className="mb-2">
        <Col xs={24} md={16} xl={15} xxl={14}>
          <h1>{t("price_lists")}</h1>
        </Col>
        <Col xs={24}>
          <Card>
            <Tabs
              activeKey={activeTab}
              onChange={(key) => {
                setActiveTab(key);
                setSearchText("");
              }}
              items={[
                {
                  key: "customer",
                  label: <span>{t("customer_price_lists")}</span>,
                },
                {
                  key: "country",
                  label: <span>{t("country_price_lists")}</span>,
                },
              ]}
            />
            {renderSearchBar()}
            {isLoading ? (
              <Loading />
            ) : (
              <div className="table-responsive">
                <Table
                  showSorterTooltip={false}
                  columns={activeTab === "customer" ? customerTableColumns : countryTableColumns}
                  dataSource={filteredPriceLists}
                  rowKey="id"
                  locale={{
                    emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("table_no_price_lists")} />,
                  }}
                />
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <AddPriceListModal visible={addModalVisible} onClose={() => setAddModalVisible(false)} />
      <AddCountryPriceListModal visible={addCountryModalVisible} onClose={() => setAddCountryModalVisible(false)} />

      <EditPriceListModal
        visible={editPriceListModalVisible}
        onClose={() => setEditPriceListModalVisible(false)}
        priceList={selectedPriceList}
        products={products}
      />

      <EditPriceListCustomersModal
        visible={editCustomersModalVisible}
        onClose={() => setEditCustomersModalVisible(false)}
        priceList={livePriceList}
        customers={customers}
      />

      {/* Details Drawer */}
      <Drawer
        title={livePriceList && <div>{livePriceList.name}</div>}
        placement="right"
        onClose={closeDrawer}
        open={drawerVisible}
        width={isMobile ? "100%" : 800}
        zIndex={1000}
      >
        {livePriceList && (
          <>
            {livePriceList.type !== "country" && (
              <>
                <Descriptions bordered size="small" column={1} title={t("assigned_customers")}>
                  <Descriptions.Item label={t("customers")} labelStyle={{ fontWeight: 600 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        {assignedCustomers.length > 0 ? (
                          <Space wrap>
                            {assignedCustomers.map((customer) => (
                              <Tag key={customer.id}>{customer.customerName}</Tag>
                            ))}
                          </Space>
                        ) : (
                          <span style={{ color: "#999", fontStyle: "italic" }}>{t("no_customers_assigned")}</span>
                        )}
                      </div>
                    </div>
                  </Descriptions.Item>
                </Descriptions>
                <ButtonsWrapper>
                  <Button onClick={() => setEditCustomersModalVisible(true)} style={{ marginBottom: 36 }}>
                    {t("edit_assigned_customers")}
                  </Button>
                </ButtonsWrapper>
              </>
            )}

            {/* Custom Prices Section */}
            <div>
              {isNetherlandsList && (
                <Alert type="info" showIcon message={t("nl_price_list_explainer")} style={{ marginBottom: 16 }} />
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ margin: 0, fontWeight: 600, fontSize: 16 }}>
                  {isNetherlandsList ? t("prices") : t("custom_prices")}
                </div>
                <Button type="default" icon={<EditOutlined />} onClick={() => setEditPriceListModalVisible(true)}>
                  {t("edit_price_list")}
                </Button>
              </div>
              {customPricedProducts.length > 0 ? (
                <Table
                  showSorterTooltip={false}
                  columns={customPriceTableColumns}
                  dataSource={customPricedProducts}
                  rowKey="id"
                  pagination={false}
                  locale={{
                    emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("no_custom_prices")} />,
                  }}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("no_custom_prices")} />
              )}
            </div>
          </>
        )}
      </Drawer>
    </>
  );
};

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
`;

const FilterPopoverContent = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
`;

const FilterButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px solid #f0f0f0;
  margin-top: 4px;
`;

export default PriceLists;
